import { Reducer } from 'redux';

import { Action } from '../actions';
import { UPDATE_COMMON_DATA } from '../actions/common';
import { UPDATE_GRID_DATA } from '../actions/grid-screen';
import {
    CHANGE_PRICES_WIZARD_TAB,
    CLEAR_PRICES_WIZARD_ERROR,
    CLOSE_PRICES_WIZARD,
    OPEN_PRICES_WIZARD,
    PRICES_WIZARD_GENERAL_ERROR,
    PRICES_WIZARD_PRICES_SAVED,
    PRICES_WIZARD_SAVE_PRICES,
    UPDATE_PRICES_WIZARD_DATA
} from '../actions/grid-wizards';
import { selectPhrasesCurrency } from '../selectors/grid/grid';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { ReducerCustom } from '../service/Redux/Redux';
import { phrases } from '../typings/common';
import { ConstData } from '../typings/constants';
import { Phrase } from '../typings/domain';
import { CurrencyCode } from '../typings/graphql-grid';
import { GridInitialData } from '../typings/grid';
import { PricesWizardData, PricesWizardType } from '../typings/inspector';
import { GridState, State } from '../typings/state';

import { getGridInitialState } from './grid/helpers';

export function getPriceWizard(gridInitialState: GridState): Reducer<GridState> {
    return (state: GridState = gridInitialState, action: Action): GridState => {
        switch (action.type) {
            case OPEN_PRICES_WIZARD:
                return {
                    ...state,
                    inspector: {
                        ...state.inspector,
                        pricesWizardOpened: true
                    },
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        error: undefined
                    }
                };
            case CLOSE_PRICES_WIZARD:
                return {
                    ...state,
                    inspector: {
                        pricesWizardOpened: false
                    },
                    pricesWizardData: getDefaultPricesWizardData(state.pricesWizardData, gridInitialState)
                };
            case PRICES_WIZARD_SAVE_PRICES:
                return {
                    ...state,
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        error: undefined,
                        isSaveInProgress: true
                    }
                };
            case PRICES_WIZARD_GENERAL_ERROR:
                return {
                    ...state,
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        error: action.message,
                        isSaveInProgress: false
                    }
                };
            case PRICES_WIZARD_PRICES_SAVED:
                return {
                    ...state,
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        isSaveInProgress: false
                    },
                    inspector: {
                        ...state.inspector,
                        pricesWizardOpened: false
                    }
                };
            case CHANGE_PRICES_WIZARD_TAB:
                return {
                    ...state,
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        wizardType: action.tab,
                        error: undefined
                    }
                };
            case UPDATE_PRICES_WIZARD_DATA:
                return {
                    ...state,
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        ...action.data,
                        error: undefined
                    }
                };

            case CLEAR_PRICES_WIZARD_ERROR:
                return {
                    ...state,
                    pricesWizardData: {
                        ...state.pricesWizardData,
                        error: undefined
                    }
                };

            default:
                return state;
        }
    };
}

const updatePrices = (
    constants: ConstData | undefined,
    currency: CurrencyCode | undefined,
    state: State,
    gridInitialState: GridState
): State => {
    if (constants === undefined || currency === undefined) {
        return state;
    }

    const price = constants.currencyConstants[currency].bigRate.toFixed(2);
    const { extended, simple } = gridInitialState.pricesWizardData;

    return {
        ...state,
        grid: {
            ...state.grid,
            pricesWizardData: {
                ...state.grid.pricesWizardData,
                wizardType: PricesWizardType.simple,
                simple: {
                    search: {
                        price,
                        checked: simple.search.checked,
                        error: undefined
                    },
                    net: {
                        price,
                        checked: simple.net.checked,
                        error: undefined
                    }
                },
                extended: {
                    search: {
                        volume: extended.search.volume,
                        increasePercentage: extended.search.increasePercentage,
                        priceLimit: price,
                        increaseError: undefined,
                        limitError: undefined,
                        checked: extended.search.checked
                    },
                    net: {
                        coverage: extended.net.coverage,
                        increasePercentage: extended.net.increasePercentage,
                        priceLimit: price,
                        increaseError: undefined,
                        limitError: undefined,
                        checked: extended.net.checked
                    },
                    mcbanner: {
                        increasePercentage: extended.mcbanner.increasePercentage,
                        priceLimit: price,
                        increaseError: undefined,
                        limitError: undefined,
                        checked: extended.mcbanner.checked
                    }
                }
            }
        }
    };
};

function getDefaultPricesWizardData(wizardData: PricesWizardData, gridInitialState: GridState): PricesWizardData {
    const defaultWizardData = gridInitialState.pricesWizardData;

    return {
        ...wizardData,
        wizardType: PricesWizardType.simple,
        simple: {
            search: {
                ...wizardData.simple.search,
                checked: defaultWizardData.simple.search.checked,
                error: undefined
            },
            net: {
                ...wizardData.simple.net,
                checked: defaultWizardData.simple.net.checked,
                error: undefined
            }
        },
        extended: {
            search: {
                ...wizardData.extended.search,
                increasePercentage: defaultWizardData.extended.search.increasePercentage,
                volume: defaultWizardData.extended.search.volume,
                checked: defaultWizardData.extended.search.checked,
                increaseError: undefined,
                limitError: undefined
            },
            net: {
                ...wizardData.extended.net,
                increasePercentage: defaultWizardData.extended.net.increasePercentage,
                coverage: defaultWizardData.extended.net.coverage,
                checked: defaultWizardData.extended.net.checked,
                increaseError: undefined,
                limitError: undefined
            },
            mcbanner: {
                ...wizardData.extended.mcbanner,
                increasePercentage: defaultWizardData.extended.mcbanner.increasePercentage,
                checked: defaultWizardData.extended.mcbanner.checked,
                increaseError: undefined,
                limitError: undefined
            }
        }
    };
}

const pricesWizardRoot = (gridInitialState: GridState): Reducer<State> => (state: State, action: Action): State => {
    switch (action.type) {
        case UPDATE_GRID_DATA: {
            const sourceParams = selectGridSourceParams(state);
            const constants = state.commonData.constants;
            const phrasesRows = action.gridData.data as Phrase[];
            if (sourceParams.view !== phrases || phrasesRows.length === 0) {
                return state;
            }
            const currency = phrasesRows[0].group.campaign.currency;
            const priceData = updatePrices(constants, currency, state, gridInitialState);

            return {
                ...priceData,
                grid: {
                    ...priceData.grid,
                    pricesWizardData: {
                        ...priceData.grid.pricesWizardData,
                        contextData: action.phraseContextData
                    }
                }
            };
        }
        case UPDATE_COMMON_DATA: {
            const constants = action.commonData.constants;
            const currency = selectPhrasesCurrency(state);

            return updatePrices(constants, currency, state, gridInitialState);
        }
        case CLOSE_PRICES_WIZARD: {
            const constants = state.commonData.constants;
            const currency = selectPhrasesCurrency(state);

            return updatePrices(constants, currency, state, gridInitialState);
        }
        default:
            return state;
    }
};

export const makePricesWizardReducer = (data: GridInitialData): ReducerCustom => {
    const gridInitialState = getGridInitialState(data);
    const pricesWizardRootReducer = pricesWizardRoot(gridInitialState);
    const pricesWizardReducer = getPriceWizard(gridInitialState);

    return (state, action, fullState): State => {
        const newState = pricesWizardRootReducer(state, action);
        const gridState = pricesWizardReducer(newState.grid, action);

        return newState.grid !== gridState
            ? {
                  ...newState,
                  grid: gridState
              }
            : newState;
    };
};
